import React, { useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';

const SKIP_LANDING_KEY = 'ummahti_skip_landing';

const AppLanding = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // On mount, check if user previously chose to skip landing
  useEffect(() => {
    // Check if we're being directed here intentionally from another component
    const fromGuestCTA = location.state?.from === 'guestcta';
    const fromGuestOverlay = location.state?.from === 'guestoverlay';
    const fromSignIn = location.state?.from === 'signin';
    const forceShow = fromGuestCTA || fromGuestOverlay || fromSignIn;

    // Only redirect if the user previously skipped the landing page AND we're not being forced to show it
    if (localStorage.getItem(SKIP_LANDING_KEY) === 'true' && !forceShow) {
      navigate('/dashboard', { replace: true });
    }
  }, [navigate, location.state]);

  // Handler for dismiss/continue button
  const handleSkipLanding = () => {
    localStorage.setItem(SKIP_LANDING_KEY, 'true');
    navigate('/dashboard', { replace: true });
  };

  return (
    <div className="flex flex-col min-h-screen relative overflow-hidden bg-[#0A0A0A]">
      {/* Background Image with Gradient Overlay */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Background Image Layer */}
        <img
          src="/images/headerbg.png"
          alt=""
          className="absolute top-0 left-1/2 -translate-x-1/2 w-[1600px] max-w-none h-full object-cover opacity-30 object-top"
        />
        {/* Primary Gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#111111] via-[#111111]/95 to-[#111111]" />
        {/* Accent Gradient */}
        <div className="absolute inset-0 bg-gradient-to-t from-transparent via-emerald-500/[0.03] to-transparent" />
      </div>

      {/* Gradient Orbs */}
      <div className="fixed top-1/4 left-1/4 w-[600px] h-[600px] rounded-full blur-3xl -translate-x-1/2 bg-emerald-500/[0.07]" />
      <div className="fixed bottom-1/4 right-1/4 w-[600px] h-[600px] rounded-full blur-3xl translate-x-1/2 bg-emerald-500/[0.07]" />

      {/* Content */}
      <div className="relative flex flex-col items-center justify-center flex-1 px-8 py-16 overflow-y-auto" 
           style={{ paddingTop: 'calc(env(safe-area-inset-top) + 6.5rem)' }}>
        {/* Logo */}
        <div className="w-32 h-32 [@media(max-height:800px)]:w-28 [@media(max-height:800px)]:h-28 [@media(max-height:650px)]:w-24 [@media(max-height:650px)]:h-24 mb-12 [@media(max-height:800px)]:mb-8 [@media(max-height:650px)]:mb-6">
          <img
            src="/images/ummahti_white_transparent.png"
            alt="Ummahti"
            className="w-full transition-opacity duration-300 opacity-90"
          />
        </div>

        {/* Welcome Text */}
        <h1 className="text-4xl [@media(max-height:800px)]:text-3xl [@media(max-height:650px)]:text-2xl font-light mb-4 [@media(max-height:650px)]:mb-2 text-center text-white">
          Welcome to Ummahti
        </h1>
        <p className="text-lg [@media(max-height:800px)]:text-base [@media(max-height:650px)]:text-sm mb-12 [@media(max-height:800px)]:mb-8 [@media(max-height:650px)]:mb-6 text-center max-w-sm text-gray-300">
          Your companion for strengthening your connection with Allah (SWT)
        </p>

        {/* Action Buttons */}
        <div className="space-y-4 [@media(max-height:650px)]:space-y-2 w-full max-w-sm">
          <button
            onClick={() => navigate('/login')}
            className="w-full py-4 [@media(max-height:800px)]:py-3 [@media(max-height:650px)]:py-2 px-6 rounded-2xl backdrop-blur-md transition-all duration-300 bg-emerald-500 hover:bg-emerald-400 text-black font-medium [@media(max-height:650px)]:text-sm"
          >
            Login
          </button>
          
          <button
            onClick={() => navigate('/signup')}
            className="w-full py-4 [@media(max-height:800px)]:py-3 [@media(max-height:650px)]:py-2 px-6 rounded-2xl backdrop-blur-md transition-all duration-300 bg-white/10 hover:bg-white/20 text-white border border-white/[0.05] [@media(max-height:650px)]:text-sm"
          >
            Create Account
          </button>

          <button
            onClick={handleSkipLanding}
            className="flex items-center justify-center gap-2 w-full py-3 [@media(max-height:800px)]:py-2 [@media(max-height:650px)]:py-1.5 px-6 mt-2 text-sm text-gray-400 hover:text-gray-300 transition-all duration-300 [@media(max-height:650px)]:text-xs"
            aria-label="Continue without logging in"
          >
            <span>Continue without logging in</span>
            <svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M8 5L13 10L8 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
      </div>

      {/* Footer Text */}
      <div className="relative pb-8 px-8 text-center" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 2rem)' }}>
        <p className="text-sm [@media(max-height:650px)]:text-xs text-gray-400">
          By continuing, you agree to our{' '}
          <Link 
            to="/terms" 
            className="text-emerald-400 hover:text-emerald-300"
          >
            Terms of Service
          </Link>{' '}
          and{' '}
          <Link 
            to="/privacy" 
            className="text-emerald-400 hover:text-emerald-300"
          >
            Privacy Policy
          </Link>
        </p>
      </div>
    </div>
  );
};

export default AppLanding; 